import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

function AdminTariffFeaturesPage() {
  const [features, setFeatures] = useState({});
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Загружаем уровни тарифов
        const levelsResponse = await fetch('/api/admin/tariff-levels', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!levelsResponse.ok) throw new Error('Не удалось загрузить уровни');
        const levelsData = await levelsResponse.json();
        setLevels(levelsData);
        
        // Загружаем функции тарифов
        const featuresResponse = await fetch('/api/admin/tariff-features', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!featuresResponse.ok) throw new Error('Не удалось загрузить функции');
        const featuresData = await featuresResponse.json();
        setFeatures(featuresData);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchData();
    }
  }, [token]);

  const handleFeatureChange = (tier, index, value) => {
    setFeatures(prev => ({
      ...prev,
      [tier]: (prev[tier] || []).map((item, i) => i === index ? value : item)
    }));
  };

  const handleAddFeature = (tier) => {
    setFeatures(prev => ({
      ...prev,
      [tier]: [...(prev[tier] || []), '']
    }));
  };

  const handleRemoveFeature = (tier, index) => {
    setFeatures(prev => ({
      ...prev,
      [tier]: (prev[tier] || []).filter((_, i) => i !== index)
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaveStatus(null);
    
    try {
      const response = await fetch('/api/admin/tariff-features', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(features)
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ошибка сохранения');
      
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (e) {
      setError(e.message);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  // Генерируем цвета для уровней
  const getTierColor = (index) => {
    const colors = [
      'var(--accent-primary)',
      'var(--accent-orange)',
      'var(--accent-purple)',
      '#10b981',
      '#3b82f6',
      '#8b5cf6',
      '#ec4899'
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <main className="admin-page-content">
        <section><div className="loading-mini">Загрузка...</div></section>
      </main>
    );
  }

  return (
    <main className="admin-page-content">
      <section>
        <h2 className="admin-page-title">Настройка функций тарифов</h2>
        <p className="admin-description-text">
          Здесь вы можете настроить список функций, которые отображаются для каждого уровня тарифа.
          Уровни тарифов можно создавать и управлять ими на странице <a href="/admin/tariff-levels" style={{ color: 'var(--primary)' }}>Уровни тарифов</a>.
        </p>
        
        {error && <div className="message-error">{error}</div>}
        
        {levels.length === 0 ? (
          <div className="empty-state">
            <p>Нет созданных уровней тарифов. Создайте уровни на странице <a href="/admin/tariff-levels" style={{ color: 'var(--primary)' }}>Уровни тарифов</a>.</p>
          </div>
        ) : (
          <form onSubmit={handleSave}>
            {levels.map((level, index) => {
              const tierCode = level.code;
              const tierFeatures = features[tierCode] || [];
              const tierColor = getTierColor(index);
              
              return (
                <div key={tierCode} className="tariff-feature-tier-card">
                  <h3 className="tariff-feature-tier-title" style={{ color: tierColor }}>
                    <span className="tier-indicator" style={{ background: tierColor }}></span>
                    {level.name} ({tierCode})
                  </h3>
              
                  <div className="tariff-feature-list">
                    {tierFeatures.map((feature, featureIndex) => (
                      <div key={featureIndex} className="tariff-feature-item">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => handleFeatureChange(tierCode, featureIndex, e.target.value)}
                          className="tariff-feature-input"
                          placeholder={`Функция ${featureIndex + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveFeature(tierCode, featureIndex)}
                          className="btn-table-delete"
                          disabled={tierFeatures.length <= 1}
                        >
                          Удалить
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => handleAddFeature(tierCode)}
                      className="btn"
                      style={{ maxWidth: '200px', marginTop: '10px' }}
                    >
                      + Добавить функцию
                    </button>
                  </div>
                </div>
              );
            })}
          
          <div className="tariff-feature-actions">
            <button 
              type="submit" 
              className="btn" 
              style={{ maxWidth: '200px' }}
              disabled={saving}
            >
              {saving ? 'Сохранение...' : 'Сохранить изменения'}
            </button>
            {saveStatus === 'success' && (
              <div className="message-success">Сохранено!</div>
            )}
          </div>
          </form>
        )}
      </section>
    </main>
  );
}

export default AdminTariffFeaturesPage;

